import SEO, { type Crumb } from '@/components/SEO';
import { Link } from 'react-router-dom';
import ServiceExperience from '@/components/services/ServiceExperience';
import ContentIndex from '@/components/ContentIndex';
import Timeline from '@/components/Timeline';
import CTABand from '@/components/CTABand';
import { contentBySection, staticPage } from '@/lib/content';

const COMPONENT_PATHS = [
  { name: 'WebCenter Content, Imaging & Capture', release: '14c', body: '14.1.2 — the out-of-place path from 12c. An 11g estate goes through 12c first.', href: '/upgrades/webcenter-14c-upgrade' },
  { name: 'SOA Suite & Service Bus', release: '14c', body: '14.1.2 — assess and refactor workflow composites, adapters and Service Bus integrations, then test the business process.', href: '/upgrades/oracle-soa-suite-12c-to-14c-upgrade' },
  { name: 'WebLogic Server', release: '14c', body: '14.1.2 — the domain beneath the applications, including JDK and datasource changes.', href: '/upgrades/oracle-weblogic-14c-upgrade' },
  { name: 'BPM Suite', release: '14c', body: '14.1.2 — process definitions, dependencies and the agreed workflow cutover approach.', href: '/upgrades/oracle-bpm-suite-12c-to-14c-upgrade' },
  { name: 'ADF applications', release: '14c', body: '14.1.2 — rebuild and test custom forms, including non-PO account coding and approval handoffs.', href: '/upgrades/coordinate-ucm-imaging-soa-upgrade' },
  { name: 'Oracle Forms', release: '14c', body: '14.1.2 (February 2025) — the path for Forms and Reports estates.', href: '/upgrades/oracle-forms-12c-to-14c-upgrade' },
  { name: 'Identity & Access Management', release: '14c', body: '14.1.2.1 (March 2025) — OAM, OIM and OUD, with their application dependencies.', href: '/upgrades/oracle-identity-management-12c-to-14c-upgrade' },
  { name: 'Data Integrator', release: '14c', body: '14.1.2 — the repository upgrade and the interfaces that need validation.', href: '/upgrades/oracle-data-integrator-12c-to-14c-upgrade' },
];

const Upgrades = () => {
  const page = staticPage('/upgrades');
  const items = contentBySection('upgrades');
  const crumbs: Crumb[] = [
    { name: 'Home', href: '/' },
    { name: 'Upgrades', href: '/upgrades' },
  ];
  return (
    <>
      <SEO title={page.title} description={page.description} path="/upgrades" breadcrumbs={crumbs} />
      <ServiceExperience
        kind="upgrades"
        crumbs={crumbs}
        label="Fusion Middleware upgrades"
        title="One estate. A coordinated path to 14c."
        lead="Content, invoice processing, integrations and the domains beneath them. We coordinate the 14c work, rehearse workflow changes and test the process users depend on."
        note="Fusion Middleware 12c: Premier Support ends December 2026. Extended Support ends December 2027. Confirm the applicable policy for each component."
      />

      <section aria-labelledby="paths-heading" className="container service-section">
        <div className="service-section-heading">
          <div><p className="label">THE COMPONENT MAP</p><h2 id="paths-heading">Every component.<br />Its own route.</h2></div>
          <p>Platform readiness and application acceptance are separate work. An invoice process also needs recognition rules, account distributions, approvals and document access tested together.</p>
        </div>
        <div className="service-route-ledger">
          {COMPONENT_PATHS.map((component) => (
            <Link key={component.name} to={component.href}>
              <span className="service-release">{component.release}</span>
              <div><h3>{component.name}</h3><p>{component.body}</p></div>
              <span aria-hidden="true">↗</span>
            </Link>
          ))}
        </div>
        <aside className="service-decision-note">
          <h3>OBIEE and Endeca need a different decision.</h3>
          <div>
            <p>Neither has a 14c release. Treat their future as a replacement decision within the wider program, with dependencies and acceptance criteria of their own.</p>
            <Link to="/upgrades/oracle-fusion-middleware-12c-end-of-support" className="service-text-link mt-4">Read the program guide <span aria-hidden="true">↗</span></Link>
            </div>
        </aside>
        <p className="service-ledger-note">
          Release numbers as published by Oracle; confirm against the current Oracle Lifetime Support Policy.
        </p>
      </section>

      <Timeline compact />

      <section aria-labelledby="guides-heading" className="container service-section">
        <div className="service-guides-heading">
          <h2 id="guides-heading">The detail behind the move.</h2>
          <p>Practical guides to platform readiness, application changes and acceptance checks across the estate.</p>
        </div>
        <div>
          <ContentIndex
            items={items}
            emptyHeading="Upgrade guides are being added."
            emptyBody="The component guides — WebCenter 14c, 11g → 14c, SOA, WebLogic, BPM, ADF, Forms, IAM, ODI, the sustaining-support decision, validation and clone-for-test — are being written now. An engineer can answer the question in the meantime."
          />
        </div>
      </section>

      <CTABand variant="engineer" />
    </>
  );
};

export default Upgrades;
